import { useState } from "react";
import { toast, Toaster } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/common/PageHero";
import { IMAGES } from "@/data/site";
import { api } from "@/lib/api";

const STEPS = ["Dados pessoais", "Informação desportiva", "Material do jogador", "Situação e objetivos"];

const initial = {
  full_name: "", birth_date: "", nationality: "", country_residence: "", city: "",
  email: "", phone: "", guardian_contact: "",
  current_club: "", previous_clubs: "", age_group: "", main_position: "", secondary_position: "",
  dominant_foot: "", height_cm: "", weight_kg: "", matches: "", goals: "", assists: "", achievements: "",
  photo_url: "", cv_url: "", video_url: "", youtube_url: "", instagram: "", transfermarkt: "", other_links: "",
  represented: "", has_contract: "", available_to_move: "", opportunity_type: "", objectives: "", how_found: "",
};

function Input({ label, name, value, onChange, type = "text", testId, required, options, textarea }) {
  return (
    <div>
      <label className="legacy-label">{label}{required && " *"}</label>
      {options ? (
        <select data-testid={testId} required={required} className="legacy-input" name={name} value={value} onChange={onChange}>
          <option value="">Selecionar</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : textarea ? (
        <textarea data-testid={testId} rows={3} className="legacy-input" name={name} value={value} onChange={onChange} />
      ) : (
        <input data-testid={testId} required={required} type={type} className="legacy-input" name={name} value={value} onChange={onChange} />
      )}
    </div>
  );
}

export default function SubmitProfile() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/applications", data);
      setDone(true);
      toast.success("Perfil enviado com sucesso.");
    } catch (err) {
      toast.error("Erro ao enviar. Verifique os campos obrigatórios (email).");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <>
        <PageHero eyebrow="Submissão" title="Perfil enviado" image={IMAGES.locker} />
        <section className="py-32">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <CheckCircle2 size={64} className="text-[#A67C00] mx-auto mb-8" strokeWidth={1} />
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight mb-6">
              Perfil enviado com sucesso
            </h2>
            <p className="text-neutral-300 text-lg leading-relaxed">
              A equipa da AF Legacy Sports irá analisar a informação submetida e entrará em contacto.
            </p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Toaster theme="dark" position="top-center" />
      <PageHero
        eyebrow="Submeter perfil"
        title={<>O teu percurso começa com uma <br /><span className="text-[#A67C00]">avaliação séria</span></>}
        subtitle="Preenche o formulário com informação verdadeira e atualizada para que a nossa equipa possa conhecer e analisar o teu perfil."
        image={IMAGES.boots}
      />

      <section className="py-16 border-b border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-4 gap-2 mb-4">
            {STEPS.map((label, i) => (
              <div key={label} className="h-[2px]" style={{ background: i <= step ? "#A67C00" : "rgba(255,255,255,0.1)" }} />
            ))}
          </div>
          <div className="flex justify-between items-center">
            <div className="section-eyebrow">Etapa {step + 1} / {STEPS.length}</div>
            <div className="font-cinzel uppercase text-white text-lg">{STEPS[step]}</div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          {step === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Nome completo" name="full_name" required value={data.full_name} onChange={set("full_name")} testId="sp-full-name" />
              <Input label="Data de nascimento" type="date" name="birth_date" value={data.birth_date} onChange={set("birth_date")} testId="sp-birth" />
              <Input label="Nacionalidade" name="nationality" value={data.nationality} onChange={set("nationality")} testId="sp-nationality" />
              <Input label="País de residência" name="country_residence" value={data.country_residence} onChange={set("country_residence")} testId="sp-country" />
              <Input label="Cidade" name="city" value={data.city} onChange={set("city")} testId="sp-city" />
              <Input label="Email" type="email" required name="email" value={data.email} onChange={set("email")} testId="sp-email" />
              <Input label="Telefone" name="phone" value={data.phone} onChange={set("phone")} testId="sp-phone" />
              <Input label="Contacto do familiar / responsável" name="guardian_contact" value={data.guardian_contact} onChange={set("guardian_contact")} testId="sp-guardian" />
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Clube atual" name="current_club" value={data.current_club} onChange={set("current_club")} testId="sp-current-club" />
              <Input label="Clubes anteriores" name="previous_clubs" value={data.previous_clubs} onChange={set("previous_clubs")} testId="sp-previous" />
              <Input label="Escalão" name="age_group" value={data.age_group} onChange={set("age_group")} testId="sp-age" />
              <Input label="Posição principal" name="main_position" value={data.main_position} onChange={set("main_position")} testId="sp-main-pos" />
              <Input label="Posição secundária" name="secondary_position" value={data.secondary_position} onChange={set("secondary_position")} testId="sp-sec-pos" />
              <Input label="Pé dominante" name="dominant_foot" options={["Direito","Esquerdo","Ambidextro"]} value={data.dominant_foot} onChange={set("dominant_foot")} testId="sp-foot" />
              <Input label="Altura (cm)" type="number" name="height_cm" value={data.height_cm} onChange={set("height_cm")} testId="sp-height" />
              <Input label="Peso (kg)" type="number" name="weight_kg" value={data.weight_kg} onChange={set("weight_kg")} testId="sp-weight" />
              <Input label="Número de jogos" type="number" name="matches" value={data.matches} onChange={set("matches")} testId="sp-matches" />
              <Input label="Golos" type="number" name="goals" value={data.goals} onChange={set("goals")} testId="sp-goals" />
              <Input label="Assistências" type="number" name="assists" value={data.assists} onChange={set("assists")} testId="sp-assists" />
              <div className="md:col-span-2">
                <Input label="Principais conquistas" textarea name="achievements" value={data.achievements} onChange={set("achievements")} testId="sp-achievements" />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Fotografia (URL)" name="photo_url" value={data.photo_url} onChange={set("photo_url")} testId="sp-photo" />
              <Input label="Currículo desportivo (URL)" name="cv_url" value={data.cv_url} onChange={set("cv_url")} testId="sp-cv" />
              <Input label="Link de vídeo" name="video_url" value={data.video_url} onChange={set("video_url")} testId="sp-video" />
              <Input label="YouTube" name="youtube_url" value={data.youtube_url} onChange={set("youtube_url")} testId="sp-youtube" />
              <Input label="Instagram" name="instagram" value={data.instagram} onChange={set("instagram")} testId="sp-instagram" />
              <Input label="Transfermarkt" name="transfermarkt" value={data.transfermarkt} onChange={set("transfermarkt")} testId="sp-tm" />
              <div className="md:col-span-2">
                <Input label="Outros links ou documentos" textarea name="other_links" value={data.other_links} onChange={set("other_links")} testId="sp-other" />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Está atualmente representado?" name="represented" options={["Sim","Não"]} value={data.represented} onChange={set("represented")} testId="sp-represented" />
              <Input label="Tem contrato com algum clube?" name="has_contract" options={["Sim","Não"]} value={data.has_contract} onChange={set("has_contract")} testId="sp-contract" />
              <Input label="Está disponível para mudar de clube?" name="available_to_move" options={["Sim","Não","A avaliar"]} value={data.available_to_move} onChange={set("available_to_move")} testId="sp-move" />
              <Input label="Que tipo de oportunidade procura?" name="opportunity_type" value={data.opportunity_type} onChange={set("opportunity_type")} testId="sp-opportunity" />
              <div className="md:col-span-2">
                <Input label="Quais são os seus objetivos?" textarea name="objectives" value={data.objectives} onChange={set("objectives")} testId="sp-objectives" />
              </div>
              <div className="md:col-span-2">
                <Input label="Como conheceu a AF Legacy Sports?" name="how_found" value={data.how_found} onChange={set("how_found")} testId="sp-how-found" />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between gap-4 mt-12">
            <button
              data-testid="sp-back"
              disabled={step === 0}
              onClick={() => setStep(step - 1)}
              className="btn-outline disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={14} /> Anterior
            </button>
            {step < STEPS.length - 1 ? (
              <button data-testid="sp-next" onClick={() => setStep(step + 1)} className="btn-gold">
                Próximo <ArrowRight size={14} />
              </button>
            ) : (
              <button data-testid="sp-submit" disabled={loading} onClick={submit} className="btn-gold">
                {loading ? "A enviar..." : "Submeter perfil para avaliação"} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
